import { interpolateGreys, schemeSet3 } from "d3-scale-chromatic";
import type { LayerProps } from "react-map-gl/mapbox";
import type { DataDrivenPropertyValueSpecification } from "mapbox-gl";

/** Fill color for aerialfarmsStd zone id 0 (unassigned). */
export const TRANSPARENT_COLOR = "rgba(0,0,0,0)";

/** Chart axis/label text color. Matches --neutral-900. */
export const BRAND_COLOR = "rgba(60, 60, 59, 1)";
/** Fill color for the lower half of a box-whisker box. Matches --neutral-400. */
export const BOX_LOWER_FILL_COLOR = "rgba(164, 165, 161, 1)";
/** Fill color for the upper half of a box-whisker box. Matches --neutral-100. */
export const BOX_UPPER_FILL_COLOR = "rgba(227, 228, 223, 1)";

/** Color stop arrays for each ortho image shader. */
export const SHADER_GRADIENTS: Record<string, { color: string; stop: number }[]> = {
	ryg: [
		{ color: "#ff0000", stop: 0 },
		{ color: "#ffff00", stop: 0.5 },
		{ color: "#00ff00", stop: 1 },
	],
	thermal: [
		{ color: "#000000", stop: 0 },
		{ color: "#4b0082", stop: 0.25 },
		{ color: "#ff0000", stop: 0.5 },
		{ color: "#ffff00", stop: 0.75 },
		{ color: "#ffffff", stop: 1 },
	],
	gfb: [
		{ color: "#000000", stop: 0 },
		{ color: "#00a9ff", stop: 0.25 },
		{ color: "#00ff00", stop: 0.5 },
		{ color: "#ffff00", stop: 0.75 },
		{ color: "#ffffff", stop: 1 },
	],
	spectral: [
		{ color: "#660000", stop: 0 },
		{ color: "#ff0000", stop: 0.12 },
		{ color: "#ff8800", stop: 0.25 },
		{ color: "#ffff00", stop: 0.5 },
		{ color: "#00ff00", stop: 0.68 },
		{ color: "#00ffff", stop: 0.85 },
		{ color: "#0000ff", stop: 1 },
	],
	by: [
		{ color: "#0000ff", stop: 0 },
		{ color: "#9d02d7", stop: 0.17 },
		{ color: "#cd34b5", stop: 0.33 },
		{ color: "#ea5f94", stop: 0.5 },
		{ color: "#fa8775", stop: 0.67 },
		{ color: "#ffb14e", stop: 0.83 },
		{ color: "#ffd700", stop: 1 },
	],
	clamped: [
		{ color: "#000000", stop: 0 },
		{ color: "#ffffff", stop: 1 },
	],
	none: [
		{ color: "#000000", stop: 0 },
		{ color: "#ffffff", stop: 1 },
	],
};

/** CSS custom property name for the field boundary stroke color (secondary gold). */
export const FIELD_BOUNDARY_CSS_VAR = "--secondary-gold-400";
/** Fallback field boundary color when the CSS variable is not resolvable (e.g. SSR or missing theme). */
export const FIELD_BOUNDARY_FALLBACK_COLOR = "rgba(255, 210, 10, 1)";

/** CSS custom property name for the plot boundary stroke color (alternate accent blue). */
export const PLOT_BOUNDARY_CSS_VAR = "--accent-alternate-UI-400";
/** Fallback plot boundary color when the CSS variable is not resolvable (e.g. SSR or missing theme). */
export const PLOT_BOUNDARY_FALLBACK_COLOR = "rgba(32, 183, 241, 1)";

/** Color values to interpolate on the trial analysis correlation matrix page. */
export const POSITIVE_CORRELATION_COLOR = { red: 89, green: 126, blue: 170 };
export const NEGATIVE_CORRELATION_COLOR = { red: 230, green: 100, blue: 101 };
export const NEUTRAL_CORRELATION_COLOR = { red: 255, green: 255, blue: 255 };

/**
 * ColorBrewer 2.0 Set3 qualitative palette for 3–12 categories, via d3-scale-chromatic.
 * schemeSet3[n] is a readonly string[] of n colors for n = 3..12.
 * Used to assign distinct categorical colors to raw/source vector data features.
 */
export const CATEGORICAL_PALETTE = schemeSet3;

/**
 * Generate a perceptually-uniform greyscale palette of `numSteps` colors, ordered dark → light.
 * `margin` insets from pure white (t=1) and pure black (t=0); defaults to 0.02 (almost full
 * range).
 */
export function generateGreyscalePalette(numSteps: number, margin = 0.02): string[] {
	if (numSteps <= 0) return [];
	if (numSteps === 1) return [interpolateGreys(1 - margin)];
	return Array.from({ length: numSteps }, (_, i) => {
		const high = 1 - margin;
		const range = 1 - 2 * margin;
		const t = high - (i / (numSteps - 1)) * range;
		return interpolateGreys(t);
	});
}

/**
 * Resolves a CSS custom property from the document root at runtime.
 * Falls back to `fallbackColor` in non-browser environments or when the variable is unset.
 *
 * Example usage:
 * `const c = resolveCssVarColor('--secondary-gold-400', 'yellow')`
 */
export function resolveCssVarColor(cssVarName: string, fallbackColor: string): string {
	if (typeof window === "undefined" || typeof document === "undefined") {
		return fallbackColor;
	}
	const value = window.getComputedStyle(document.documentElement).getPropertyValue(cssVarName).trim();
	return value || fallbackColor;
}

/**
 * Returns Mapbox fill LayerProps for rendering zone/feature polygons.
 * `colorMatchExpr` should be a Mapbox `match` expression mapping feature property values to colors.
 * Fill opacity is fixed at 0.9.
 */
export function makeFeatureFillLayerProps(
	id: string,
	colorMatchExpr: DataDrivenPropertyValueSpecification<string>,
): LayerProps {
	return {
		id,
		type: "fill",
		paint: {
			"fill-color": colorMatchExpr,
			"fill-opacity": 0.9,
		},
	};
}

/**
 * Returns Mapbox line LayerProps for rendering polygon outlines (field/plot boundaries, zone edges).
 * Line width is fixed at 2px, opacity at 0.9.
 * Omit `id` when the caller sets it dynamically via JSX props (e.g. per-feature layers in a map).
 */
export function makeFeatureOutlineLayerProps(id: string | undefined, lineColor: string): LayerProps {
	return {
		...(id !== undefined && { id }),
		type: "line",
		paint: {
			"line-color": lineColor,
			"line-width": 2,
			"line-opacity": 0.9,
		},
	};
}

/**
 * Returns Mapbox symbol LayerProps for rendering zone value labels at polygon centroids.
 * Text is read from the `zoneValueLabel` feature property.
 * White text with black halo; size scales with zoom.
 */
export function makeValueLabelLayerProps(id: string): LayerProps {
	return {
		id,
		type: "symbol",
		layout: {
			"text-field": ["get", "zoneValueLabel"],
			"text-size": ["interpolate", ["linear"], ["zoom"], 12, 0, 13, 10, 17, 16],
			"text-allow-overlap": false,
			"text-ignore-placement": false,
		},
		paint: {
			"text-color": "#ffffff",
			"text-halo-color": "#000000",
			"text-halo-width": 1,
		},
	};
}
